import { guestApi } from '../../api/guest';
import { sedrApi, type SedrPhase, type SedrRequirement } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { iconImg } from '../../ui/icons';
import { phaseOptionHtml } from './sedrPhases';
import { sedrErrorMessageKey } from './sedrErrors';
import { escapeHtml } from './sedrEscape';
import {
  getCachedSedrCategories,
  loadSedrCategories,
  renderCategorySelectOptions,
  tSedrCategory,
  tSedrStatus,
} from './sedrUi';
import {
  intersectStatusActions,
  statusForAction,
  type SedrStatusAction,
} from './sedrRequirementTransitions';
import { showInputDialog } from '../../ui/inputDialog';

const CODE_LIST_LIMIT = 10;

export interface SedrBulkRequirementPanelOptions {
  roomId: string;
  requirements: SedrRequirement[];
  phases: SedrPhase[];
  canWrite: boolean;
  onUpdated: () => Promise<void> | void;
  onClose?: () => void;
  guest?: {
    roomToken?: string;
    guestInviteId?: string;
    accountRoomId?: string;
    canApprove: boolean;
  };
}

function countBy<T extends string>(items: T[]): Map<T, number> {
  const map = new Map<T, number>();
  for (const item of items) {
    map.set(item, (map.get(item) ?? 0) + 1);
  }
  return map;
}

function breakdownHtml(
  counts: Map<string, number>,
  labelFor: (key: string) => string,
): string {
  const parts = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => `<li><span>${escapeHtml(labelFor(key))}</span><strong>${count}</strong></li>`);
  return parts.length > 0 ? `<ul class="sedr-bulk-panel__breakdown">${parts.join('')}</ul>` : '';
}

/**
 * Multi-select summary + bulk category/phase/status edits (transactional API).
 */
export async function renderSedrBulkRequirementPanel(
  host: HTMLElement,
  options: SedrBulkRequirementPanelOptions,
): Promise<void> {
  const { roomId, requirements, phases, canWrite, guest } = options;
  const selected = requirements;
  const categories = await loadSedrCategories(roomId).catch(() => getCachedSedrCategories());

  const statusCounts = countBy(selected.map((r) => r.status));
  const categoryCounts = countBy(selected.map((r) => r.category));
  const phaseCounts = countBy(selected.map((r) => r.phaseId ?? ''));

  const canApproveBulk = guest
    ? Boolean(guest.canApprove) && selected.every((r) => r.canClientApprove)
    : selected.every((r) => r.canApprove);

  const hasLifecycleRequest = selected.some((r) => Boolean(r.requestedLifecycleStatus));
  const anyApproved = selected.some((r) => r.status === 'ApprovedDischarged');
  const statusActions = hasLifecycleRequest
    ? ([] as SedrStatusAction[])
    : intersectStatusActions(
      selected.map((r) => r.status),
      canApproveBulk,
      selected.map((r) => r.renewalStatus),
    ).filter((action) => {
      if (action === 'markReady' || action === 'withdraw') {
        return canWrite && !guest;
      }
      // approve/deny: members via bulk PATCH; guests via guest bulk endpoints
      if (guest) return action === 'approve' || action === 'deny';
      return canWrite;
    });

  const actionButton = (action: SedrStatusAction): string => {
    const id = `sedr-bulk-${action}`;
    const i18n =
      action === 'markReady' ? 'sedr.drawer.markAsReady'
        : action === 'withdraw' ? 'sedr.drawer.withdraw'
          : action === 'deny' ? 'sedr.drawer.deny'
            : guest ? 'sedr.guest.signOff' : 'sedr.drawer.approve';
    const cls = action === 'approve' ? 'button primary' : 'button subtle';
    return `<button type="button" class="${cls}" id="${id}" data-i18n="${i18n}"></button>`;
  };

  const codesList = selected.length <= CODE_LIST_LIMIT
    ? `<ul class="sedr-bulk-panel__codes">${selected
      .map((r) => `<li><code>${escapeHtml(r.requirementCode)}</code></li>`)
      .join('')}</ul>`
    : '';

  const phaseLabel = (phaseId: string) => {
    if (!phaseId) return t('sedr.phases.unassigned');
    return phases.find((p) => p.id === phaseId)?.name ?? phaseId;
  };

  host.className = 'sedr-requirement-panel-host sedr-requirement-panel sedr-bulk-panel';
  host.innerHTML = `
    <header class="sedr-requirement-panel__header">
      ${options.onClose
        ? `<button type="button" class="button ghost sedr-requirement-panel__close" id="sedr-bulk-close" data-i18n-aria-label="common.back"></button>`
        : ''}
      <div class="sedr-requirement-panel__title-block">
        <h2 data-i18n="sedr.bulk.title"></h2>
        <p class="muted sedr-bulk-panel__count">${escapeHtml(t('sedr.bulk.selectedCount', { count: selected.length }))}</p>
      </div>
    </header>

    <p class="muted sedr-bulk-panel__hint" data-i18n="sedr.bulk.selectionHint"></p>
    ${codesList}

    <section class="sedr-requirement-panel__section">
      <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.table.status"></h3>
      ${breakdownHtml(statusCounts, tSedrStatus)}
    </section>

    <section class="sedr-requirement-panel__section">
      <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.table.category"></h3>
      ${breakdownHtml(categoryCounts, (id) => tSedrCategory(id, categories))}
    </section>

    <section class="sedr-requirement-panel__section">
      <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.phases.field"></h3>
      ${breakdownHtml(phaseCounts, phaseLabel)}
    </section>

    ${canWrite && !guest && !anyApproved ? `
    <section class="sedr-requirement-panel__section sedr-bulk-panel__edit">
      <h3 class="sedr-requirement-panel__section-title" data-i18n="sedr.bulk.editTitle"></h3>
      <p class="muted sedr-bulk-panel__mixed" data-i18n="sedr.bulk.mixedHint"></p>
      <div class="sedr-bulk-panel__field-row">
        <label>
          <span data-i18n="sedr.requirement.category"></span>
          <select id="sedr-bulk-category"></select>
        </label>
        <button type="button" class="button subtle" id="sedr-bulk-apply-category" data-i18n="sedr.bulk.apply"></button>
      </div>
      <div class="sedr-bulk-panel__field-row">
        <label>
          <span data-i18n="sedr.phases.field"></span>
          <select id="sedr-bulk-phase">${phaseOptionHtml(phases, null)}</select>
        </label>
        <button type="button" class="button subtle" id="sedr-bulk-apply-phase" data-i18n="sedr.bulk.apply"></button>
      </div>
    </section>` : ''}

    ${statusActions.length > 0 ? `
    <div class="sedr-requirement-panel__toolbar sedr-bulk-panel__actions" id="sedr-bulk-toolbar">
      ${statusActions.map(actionButton).join('')}
    </div>` : ''}

    <p class="error-text" id="sedr-bulk-error" hidden></p>
  `;
  applyI18n(host);

  const bulkCloseBtn = host.querySelector('#sedr-bulk-close') as HTMLButtonElement | null;
  if (bulkCloseBtn && options.onClose) {
    bulkCloseBtn.append(iconImg('chevron-left', 18));
    bulkCloseBtn.addEventListener('click', () => options.onClose?.());
  }

  const categorySelect = host.querySelector('#sedr-bulk-category') as HTMLSelectElement | null;
  if (categorySelect) {
    const dominantCategory = [...categoryCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    renderCategorySelectOptions(categorySelect, categories, dominantCategory);
  }

  const errorEl = host.querySelector('#sedr-bulk-error') as HTMLParagraphElement;
  let busy = false;

  const showError = (cause: unknown) => {
    errorEl.hidden = false;
    errorEl.textContent = t(sedrErrorMessageKey(cause));
  };

  const runBulk = async (work: () => Promise<void>) => {
    if (busy) return;
    busy = true;
    errorEl.hidden = true;
    try {
      await work();
      await options.onUpdated();
    } catch (cause) {
      showError(cause);
    } finally {
      busy = false;
    }
  };

  const ids = selected.map((r) => r.id);

  host.querySelector('#sedr-bulk-apply-category')?.addEventListener('click', () => {
    const category = categorySelect?.value?.trim();
    if (!category) return;
    void runBulk(async () => {
      await sedrApi.bulkPatchRequirements(roomId, { requirementIds: ids, category });
    });
  });

  host.querySelector('#sedr-bulk-apply-phase')?.addEventListener('click', () => {
    const phaseSelect = host.querySelector('#sedr-bulk-phase') as HTMLSelectElement | null;
    const phaseId = phaseSelect ? String(phaseSelect.value) : '';
    void runBulk(async () => {
      await sedrApi.bulkPatchRequirements(roomId, { requirementIds: ids, phaseId });
    });
  });

  const patchStatus = (action: SedrStatusAction) => {
    void runBulk(async () => {
      const status = statusForAction(action);
      if (guest && (action === 'approve' || action === 'deny')) {
        if (action === 'approve') {
          if (guest.accountRoomId) {
            await guestApi.bulkApproveRequirements(guest.accountRoomId, ids);
          } else {
            await sedrApi.guestBulkApproveRequirements(guest.roomToken!, guest.guestInviteId!, ids);
          }
        } else {
          const reason = await showInputDialog({
            titleKey: 'sedr.drawer.deny',
            labelKey: 'sedr.drawer.denyReasonPrompt',
            confirmLabelKey: 'sedr.drawer.deny',
            multiline: true,
            required: true,
            maxLength: 2000,
          });
          if (!reason) {
            return;
          }
          if (guest.accountRoomId) {
            await guestApi.bulkDenyRequirements(guest.accountRoomId, ids, reason);
          } else {
            await sedrApi.guestBulkDenyRequirements(guest.roomToken!, guest.guestInviteId!, ids, reason);
          }
        }
        return;
      }

      let statusChangeReason: string | undefined;
      if (action === 'deny') {
        const reason = await showInputDialog({
          titleKey: 'sedr.drawer.deny',
          labelKey: 'sedr.drawer.denyReasonPrompt',
          confirmLabelKey: 'sedr.drawer.deny',
          multiline: true,
          required: true,
          maxLength: 2000,
        });
        if (!reason) {
          return;
        }
        statusChangeReason = reason;
      }

      await sedrApi.bulkPatchRequirements(roomId, {
        requirementIds: ids,
        status,
        statusChangeReason,
      });
    });
  };

  host.querySelector('#sedr-bulk-markReady')?.addEventListener('click', () => patchStatus('markReady'));
  host.querySelector('#sedr-bulk-withdraw')?.addEventListener('click', () => patchStatus('withdraw'));
  host.querySelector('#sedr-bulk-deny')?.addEventListener('click', () => patchStatus('deny'));
  host.querySelector('#sedr-bulk-approve')?.addEventListener('click', () => patchStatus('approve'));
}
